import type {
  MemoryConstructionParams,
  MemoryGroup,
  MemoryLane,
} from '@timelyapp/integration-cloud-api-spec';
import get from 'lodash-es/get';
import orderBy from 'lodash-es/orderBy';

export const MIN_PX_GAP_BETWEEN_MEMORIES = 20;

/**
 * This function is responsible for constructing sub-lanes based on time. It helps in organizing memories
 * in such a way that there is no overlap of memories occurring at the same time.
 * This will be used as a utility function in both constructLanesByCategory and constructLanesByTime.
 *
 * @param memoryGroups - Array of MemoryGroup which needs to be arranged in sub-lanes.
 * @param memoryQueries - The MemoryConstructionParams object which contains the necessary properties to guide the construction of the lanes.
 * @param toolConfig - Optional ToolConfig for dedicated lane handling
 *
 * @returns Returns an array of arrays where each sub-array represents a sub-lane of memories organized based on time.
 */

interface MemoryAndLayoutData {
  memory: MemoryGroup;
  offset: number;
  length: number;
}

export function constructSubLanesByTime(
  memoryGroups: MemoryGroup[],
  memoryQuery: MemoryConstructionParams
): MemoryGroup[][] {
  // Handle empty input case
  if (memoryGroups.length === 0) {
    return [[]];
  }

  const minDurationInSeconds = memoryQuery.minimumDurationMinutes * 60;

  // Get the tool identifier from the first memory in the group (if any)
  const toolIdentifier =
    memoryGroups.length > 0 ? memoryGroups[0].tool?.id || memoryGroups[0].tool?.raw : undefined;

  // Look up the tool config from toolConfigsMap if a tool identifier exists
  const toolConfig = toolIdentifier ? memoryQuery.toolConfigsMap[toolIdentifier] : undefined;
  const isDedicatedLane = !!toolConfig?.preferences?.dedicated_lane;

  const minGapInSeconds = isDedicatedLane
    ? // We allow 0 gap between memories in dedicated lanes,
      // so e.g 2 meetings back-to-back don't force a split into 2 lanes.
      0
    : // For non-dedicated lanes, ensure there's always a 20px gap between memories
      (MIN_PX_GAP_BETWEEN_MEMORIES / memoryQuery.pxPerMinute) * 60; // Convert 20px to minutes, then to seconds

  // For dedicated lanes, we want FIRST lane to be populated by first occurring memory,
  // while "other memories" benefits from having largest memories furthest to left.
  // (for no other reason than it felt better while developing; can be changed)
  const sortedMemories = isDedicatedLane
    ? orderBy(memoryGroups, [memory => +new Date(memory.started_at)], ['asc'])
    : orderBy(
        memoryGroups,
        [memory => memory.total_seconds, memory => +new Date(memory.started_at)],
        ['desc', 'asc']
      );

  // Initialize lanes array
  const lanes: Array<Array<MemoryAndLayoutData>> = [[]];

  // Process each memory
  for (const memory of sortedMemories) {
    const fromSeconds = +new Date(memory.started_at).getTime() / 1000;
    const toSeconds = +new Date(memory.ended_at).getTime() / 1000;

    const memoryWithOffset: MemoryAndLayoutData = {
      memory,
      offset: fromSeconds,
      length: Math.max(minDurationInSeconds, toSeconds - fromSeconds),
    };

    // Try to find an existing lane where this memory fits
    let placedInExistingLane = false;

    for (let i = 0; i < lanes.length; i++) {
      let canFitInLane = true;

      for (const existingMemory of lanes[i]) {
        const existingStart = existingMemory.offset;
        const existingEnd = existingMemory.offset + existingMemory.length;
        const memoryStart = memoryWithOffset.offset;
        const memoryEnd = memoryWithOffset.offset + memoryWithOffset.length;

        // For dedicated lanes, check if memories are back-to-back
        if (isDedicatedLane) {
          // If memories are back-to-back, they can be in the same lane
          const memory1End = new Date(existingMemory.memory.ended_at).getTime();
          const memory2Start = new Date(memory.started_at).getTime();
          const memory2End = new Date(memory.ended_at).getTime();
          const memory1Start = new Date(existingMemory.memory.started_at).getTime();

          const areBackToBack = memory1End === memory2Start || memory2End === memory1Start;

          if (areBackToBack) {
            continue;
          }
        }

        // Check for overlap with minimum gap
        const hasOverlap =
          memoryStart - minGapInSeconds <= existingEnd &&
          memoryEnd + minGapInSeconds >= existingStart;

        if (hasOverlap) {
          canFitInLane = false;
          break;
        }
      }

      if (canFitInLane) {
        lanes[i].push(memoryWithOffset);
        placedInExistingLane = true;
        break;
      }
    }

    // If no existing lane works, create a new lane
    if (!placedInExistingLane) {
      lanes.push([memoryWithOffset]);
    }
  }

  // Return the lanes with only the memory data, sorted by start time within each lane
  return lanes.map(lane =>
    lane
      .map(({ memory }) => memory)
      .sort((a, b) => {
        return +new Date(a.started_at) - +new Date(b.started_at);
      })
  );
}

/**
 * Function to construct lanes based on different strategies: 'time', 'tool', 'category'
 * @param memoryGroups - Array of Memories to be organized into lanes
 * @param memoryQueries - The MemoryConstructionParams object which contains the necessary properties to guide the construction of the lanes.
 * @returns Constructed lanes as per the specified strategy
 */
export function constructMemoryLanes(
  memoryGroups: MemoryGroup[],
  memoryQueries: MemoryConstructionParams
): MemoryLane[] {
  const mainLanes: MemoryLane[] = [];
  let groupsForTimeBasedLane: MemoryGroup[] = [];
  let groupsForStarredLane: MemoryGroup[] = [];

  const { constructLanesOn, toolConfigsMap } = memoryQueries;

  // Grouping memories based on the selected property, tool.raw is always a fallback
  const groupKey = constructLanesOn === 'category' ? 'category.id' : 'tool.id';
  const memoriesGroupedMap = groupMemoriesByProperty(memoryGroups, groupKey);

  // NB: `sticky` lanes force `dedicated_lane: true`
  const dedicatedToolLanes = Object.keys(toolConfigsMap)
    ?.filter(mapKey => toolConfigsMap[mapKey]?.preferences?.dedicated_lane)
    .map(mapKey => toolConfigsMap[mapKey]);

  // Initializing the base structure of the time-based lane
  const timeBasedLane: MemoryLane = {
    type: 'time',
    title: 'Memories',
    sub_lanes: [],
    total_seconds: 0,
    sticky: false,
    compact: memoryQueries.compact,
    ignored: false,
    dedicated_lane: false,
  };

  // If user has configured tools to be stick/compact/etc, create special lanes for them
  dedicatedToolLanes?.forEach(toolConfig => {
    const toolIdentifier = toolConfig?.tool_id || toolConfig?.tool_raw;
    const currentGroup = toolIdentifier ? memoriesGroupedMap[toolIdentifier] : undefined;
    const groupBy =
      toolConfig?.preferences?.group_by === 'default'
        ? memoryQueries.groupMemoriesBy
        : toolConfig?.preferences?.group_by || memoryQueries.groupMemoriesBy;

    const compact = toolConfig ? toolConfig.preferences?.compact : memoryQueries.compact;

    if (currentGroup) {
      const subLanes =
        // Grouping by `tool` doesn't have overlapping memories, so we don't need sub-lanes
        // todo: not always true, title grouping can look iffy without sublanes, re-visit
        groupBy !== 'tool' ? constructSubLanesByTime(currentGroup, memoryQueries) : [currentGroup];

      mainLanes.push({
        id: constructLanesOn === 'category' ? currentGroup[0].category?.id : toolIdentifier,
        type: constructLanesOn === 'category' ? 'category' : 'tool',
        title: currentGroup[0].tool?.title || '',
        icon_url: currentGroup[0].icon_url || '',
        sub_lanes: subLanes,
        total_seconds: calculateTotalSeconds(currentGroup),
        sticky: !!toolConfig?.preferences?.sticky,
        compact,
        ignored: !!toolConfig?.preferences?.ignored,
        dedicated_lane: !!toolConfig?.preferences?.dedicated_lane,
      });

      // Removing the processed group from the memoriesGroupedMap object
      if (toolIdentifier) delete memoriesGroupedMap[toolIdentifier];
    }
  });

  for (const groupByKey in memoriesGroupedMap) {
    if (groupByKey === 'no_group_key') {
      continue;
    }

    const currentGroup = memoriesGroupedMap[groupByKey];
    const totalSeconds = calculateTotalSeconds(currentGroup);

    const toolConfig = toolConfigsMap[groupByKey] || undefined;
    const isStarred = !!toolConfig?.preferences?.starred;

    // Checks to determine if we're building a time-based lane, or dedicated one.
    const useTimeBasedLane = constructLanesOn === 'time';
    const dedicatedLane = !!toolConfig?.preferences?.dedicated_lane;
    const belowAutoDedicatedLaneThreshold =
      totalSeconds < memoryQueries.minMinutesForDedicatedLane * 60;
    const deferToTimeBasedLane =
      useTimeBasedLane || (!dedicatedLane && belowAutoDedicatedLaneThreshold);

    if (deferToTimeBasedLane) {
      // If the tool is starred, add to starred lane instead of time-based lane
      if (isStarred) {
        groupsForStarredLane = groupsForStarredLane.concat(currentGroup);
      } else {
        groupsForTimeBasedLane = groupsForTimeBasedLane.concat(currentGroup);
      }
    } else {
      // Adding the group as a separate lane in the main lanes array
      const groupBy =
        toolConfig?.preferences?.group_by === 'default'
          ? memoryQueries.groupMemoriesBy
          : toolConfig?.preferences?.group_by || memoryQueries.groupMemoriesBy;

      const compact = toolConfig ? toolConfig.preferences?.compact : memoryQueries.compact;

      mainLanes.push({
        type: constructLanesOn,
        title: currentGroup[0][constructLanesOn as 'tool' | 'category']?.title || '',
        icon_url: currentGroup[0][constructLanesOn as 'tool' | 'category']?.icon_url || '',
        sub_lanes:
          groupBy !== 'tool'
            ? constructSubLanesByTime(currentGroup, memoryQueries)
            : [currentGroup],
        total_seconds: totalSeconds,
        sticky: toolConfig?.preferences?.sticky || false,
        compact,
        ignored: toolConfig?.preferences?.ignored || false,
        dedicated_lane: toolConfig?.preferences?.dedicated_lane || false,
      });
    }
  }

  if (memoriesGroupedMap['no_group_key']) {
    groupsForTimeBasedLane = groupsForTimeBasedLane.concat(memoriesGroupedMap['no_group_key']);
  }

  // Create starred lane if there are starred memory groups
  if (groupsForStarredLane.length > 0) {
    const starredLane: MemoryLane = {
      type: 'starred',
      title: 'Starred',
      sub_lanes: constructSubLanesByTime(groupsForStarredLane, memoryQueries),
      total_seconds: calculateTotalSeconds(groupsForStarredLane),
      sticky: false,
      compact: memoryQueries.compact,
      ignored: false,
      dedicated_lane: false,
    };
    mainLanes.push(starredLane);
  }

  // If we're showing "other memories lane", add the time-based lane
  if (memoryQueries.showOtherMemoriesLane) {
    timeBasedLane.sub_lanes = constructSubLanesByTime(groupsForTimeBasedLane, memoryQueries);
    timeBasedLane.total_seconds = calculateTotalSeconds(groupsForTimeBasedLane);
    if (Array.isArray(timeBasedLane.sub_lanes)) {
      mainLanes.push(timeBasedLane);
    }
    // Otherwise, we need placeholder lane for spacing
  } else {
    const emptyPlaceholderLane: MemoryLane = {
      type: 'time',
      title: '',
      sub_lanes: [],
      total_seconds: 0,
      sticky: false,
      compact: false,
      ignored: false,
      dedicated_lane: false,
    };
    mainLanes.push(emptyPlaceholderLane);
  }

  return sortMemoryLanesByDuration(mainLanes);
}

// -----
// HELPER FUNCTIONS
// -----

export function groupMemoriesByProperty(
  memoryGroups: MemoryGroup[],
  propertyPath: 'tool.id' | 'category.id'
): Record<string, MemoryGroup[]> {
  return memoryGroups.reduce(
    (acc, memory) => {
      // Group the memory into groups by these values, with `tool.raw` as a fallback
      const value = get(memory, propertyPath) || get(memory, 'tool.raw');
      if (value) {
        if (!acc[value]) acc[value] = [];
        acc[value].push(memory);
      } else {
        // If the memory does not have a tool associated, let's give it a default group
        if (!acc['no_group_key']) acc['no_group_key'] = [];
        acc['no_group_key'].push(memory);
      }
      return acc;
    },
    {} as Record<string, MemoryGroup[]>
  );
}

/**
 * Calculate total memory duration of a group of memories (like a lane),
 * excluding overlapping time.
 * */
export function calculateTotalSeconds(memoryGroups: MemoryGroup[]): number {
  // Helper interface
  interface TimeRange {
    start: number;
    end: number;
  }

  // Flatten all memories and groups into a single array of time ranges
  const timeRanges: TimeRange[] = memoryGroups.flatMap(group => {
    const ranges: TimeRange[] = [];

    if (group.started_at && group.ended_at) {
      // If the group has no memories, treat the group itself as group:
      // Add group range if it has start and end times
      ranges.push({
        start: new Date(group.started_at).getTime(),
        end: new Date(group.ended_at).getTime(),
      });
    } else if (group.memories) {
      // Add ranges for each memory in the group
      ranges.push(
        ...group.memories
          .filter(mem => mem.started_at && mem.ended_at)
          .map(mem => ({
            start: new Date(mem.started_at).getTime(),
            end: new Date(mem.ended_at).getTime(),
          }))
      );
    }

    return ranges;
  });

  // Sort time ranges by start time
  timeRanges.sort((a, b) => a.start - b.start);

  let totalMilliseconds = 0;
  let currentEnd = 0;

  // Iterate through sorted ranges and calculate non-overlapping time
  for (const range of timeRanges) {
    if (range.start > currentEnd) {
      // No overlap, add the entire range
      totalMilliseconds += range.end - range.start;
      currentEnd = range.end;
    } else if (range.end > currentEnd) {
      // Partial overlap, add only the non-overlapping part
      totalMilliseconds += range.end - currentEnd;
      currentEnd = range.end;
    }
    // If range.end <= currentEnd, it's completely overlapped, so we ignore it
  }

  // Convert milliseconds to seconds and return
  return Math.round(totalMilliseconds / 1000);
}
/**
 * Returns an array of MemoryLane sorted by their total_seconds
 * in descending order, with `time` lanes always at the end.
 */
export function sortMemoryLanesByDuration(lanes: MemoryLane[]): MemoryLane[] {
  return lanes.sort((a, b) => {
    // Compact lanes always
    // if (a.compact && !b.compact) return -1;
    // if (b.compact && !a.compact) return 1;

    // Time lane always last
    if (a.type === 'time' && b.type !== 'time') return 1;
    if (b.type === 'time' && a.type !== 'time') return -1;

    // Starred lane before time lane but after other lanes
    if (a.type === 'starred' && b.type !== 'time' && b.type !== 'starred') return 1;
    if (b.type === 'starred' && a.type !== 'time' && a.type !== 'starred') return -1;
    if (a.type === 'starred' && b.type === 'time') return -1;
    if (b.type === 'starred' && a.type === 'time') return 1;

    return b.total_seconds - a.total_seconds;
  });
}
